/**
 * Dialog: Scan ARP table dari MikroTik untuk deteksi pelanggan static-IP.
 * Trigger: tombol "Scan IP dari Router" di halaman Pelanggan → Statik/DHCP.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Router, Search, CheckCircle, AlertCircle, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScanArpDialog = ({ open, onClose, onLinkCustomer }) => {
  const [routers, setRouters] = useState([]);
  const [selectedRouter, setSelectedRouter] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('all'); // all | matched | unmatched
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/v2/mikrotiks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const items = r.data?.data || r.data || [];
        setRouters(Array.isArray(items) ? items : []);
      })
      .catch(() => setRouters([]));
  }, [open]);

  const handleScan = async () => {
    if (!selectedRouter) return;
    setScanning(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/v2/mikrotik/${selectedRouter}/scan-arp`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setResult(r.data);
    } catch (e) {
      setResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setScanning(false);
    }
  };

  const filteredEntries = (result?.entries || []).filter(e => {
    if (filter === 'matched' && !e.customer_id) return false;
    if (filter === 'unmatched' && e.customer_id) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.ip.toLowerCase().includes(q) ||
             (e.mac || '').toLowerCase().includes(q) ||
             (e.customer_name || '').toLowerCase().includes(q) ||
             (e.customer_account || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Router className="w-5 h-5 text-blue-600" />
            Scan IP Statik dari Router
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <select
            data-testid="scan-router-select"
            value={selectedRouter || ''}
            onChange={(e) => setSelectedRouter(e.target.value)}
            className="flex-1 p-2 border border-slate-300 rounded-md bg-white text-sm"
          >
            <option value="">-- Pilih Router --</option>
            {routers.map(r => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.host})
              </option>
            ))}
          </select>
          <Button
            data-testid="scan-arp-button"
            onClick={handleScan}
            disabled={!selectedRouter || scanning}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : <><Search className="w-4 h-4 mr-2" /> Scan ARP</>}
          </Button>
        </div>

        {result?.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <span className="text-sm text-red-700">{result.error}</span>
          </div>
        )}

        {result?.success && (
          <>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <div className="text-blue-600 font-semibold">Total ARP</div>
                <div className="text-xl font-bold text-blue-900">{result.total_arp_entries}</div>
              </div>
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                <div className="text-emerald-600 font-semibold">Cocok Pelanggan</div>
                <div className="text-xl font-bold text-emerald-900">{result.matched_customers}</div>
              </div>
              <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <div className="text-amber-600 font-semibold">Belum Terdaftar</div>
                <div className="text-xl font-bold text-amber-900">{result.total_arp_entries - result.matched_customers}</div>
              </div>
            </div>

            <div className="flex gap-2 items-center">
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                {['all','matched','unmatched'].map(f => (
                  <button
                    key={f}
                    data-testid={`filter-${f}`}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1 text-xs rounded-md transition ${filter===f ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-600 hover:bg-white/60'}`}
                  >
                    {f === 'all' ? 'Semua' : f === 'matched' ? 'Cocok' : 'Belum Terdaftar'}
                  </button>
                ))}
              </div>
              <Input
                data-testid="scan-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari IP / MAC / nama pelanggan..."
                className="flex-1 h-8 text-xs"
              />
            </div>

            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-2">IP Address</th>
                    <th className="text-left p-2">MAC Address</th>
                    <th className="text-left p-2">Interface</th>
                    <th className="text-left p-2">Pelanggan</th>
                    <th className="text-left p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length === 0 ? (
                    <tr><td colSpan="5" className="p-6 text-center text-slate-400">Tidak ada data</td></tr>
                  ) : (
                    filteredEntries.map((e, i) => (
                      <tr key={i} className={`border-t border-slate-100 hover:bg-slate-50 ${e.customer_id ? 'bg-emerald-50/40' : ''}`}>
                        <td className="p-2 font-mono text-slate-900">{e.ip}</td>
                        <td className="p-2 font-mono text-slate-600">{e.mac || '-'}</td>
                        <td className="p-2 text-slate-500">{e.interface}</td>
                        <td className="p-2">
                          {e.customer_id ? (
                            <div>
                              <div className="font-medium text-emerald-700">{e.customer_name}</div>
                              <div className="text-[10px] text-slate-500">#{e.customer_account}</div>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">—</span>
                          )}
                        </td>
                        <td className="p-2">
                          {e.customer_id ? (
                            e.mac_matches_customer ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-semibold">
                                <CheckCircle className="w-3 h-3" /> MAC Cocok
                              </span>
                            ) : e.mac ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-semibold">
                                <CheckCircle className="w-3 h-3" /> IP Terdaftar
                              </span>
                            ) : null
                          ) : e.is_dynamic ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px]">
                              Dynamic
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">
                              Static (belum daftar)
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button onClick={onClose} variant="outline" data-testid="scan-close">Tutup</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScanArpDialog;
